'use client';

import React, {
  useMemo,
  useState,
  useRef,
  useEffect,
  useCallback,
} from 'react';
import { FontAwesomeIcon as Icon } from '@fortawesome/react-fontawesome';
import { faList, faPlus, faSearch, faUserDoctor } from '@fortawesome/pro-solid-svg-icons';
import { faMap } from '@fortawesome/pro-regular-svg-icons';
import { StrategyDropdown } from '../StrategyDropdown';
import { StrategyTerritoryDropdown } from '../StrategyTerritoryDropdown';

import {
  StrategyProviderSearch,
  StrategyProviderListDropdown,
  StrategySpecialtyDropdown,
  StrategySiteOfCareTypeDropdown,
  StrategyStatusDropdown,
  StrategyCityDropdown,
  StrategyStateDropdown,
} from '../StrategyFilters';
import { StrategyInfoButton } from '../StrategyInfoButton';
import { TableColumnMenu } from 'src/components/Tables/components/TableColumnMenu';
import { strategyListTableColumns } from 'src/components/Tables/components/Columns/ColumnDefinitions';
import { useLogging } from 'src/context/logging';
import { LOCATION } from 'src/hooks/filters/constants';
import useStrategies from 'src/hooks/strategy/useStrategies';

const STRATEGY_TOGGLEABLE_COLUMNS = [
  'crm_links',
  'chain_ids',
  'tags',
  'specialty',
  'soc_type',
  'city',
  'affiliations',
  'list_membership',
];

type View = 'map' | 'list';

const VIEW_OPTIONS: Array<{ key: View; label: string; icon: typeof faList }> = [
  { key: 'list', label: 'List', icon: faList },
  { key: 'map', label: 'Map', icon: faMap },
];

interface StrategyCenterHeaderProps {
  view: View;
  setView: (view: View) => void;
  search: string;
  onSearchChange: (search: string) => void;
  onAddToList: () => void;
  savedColumnVisibility: Record<string, boolean>;
  onColumnVisibilityChange: React.Dispatch<
    React.SetStateAction<Record<string, boolean>>
  >;
  listCount: { total: number; isFetching: boolean };
}

const StrategyCenterHeader = ({
  view,
  setView,
  search,
  onSearchChange,
  onAddToList,
  savedColumnVisibility,
  onColumnVisibilityChange,
  listCount,
}: StrategyCenterHeaderProps) => {
  const log = useLogging();
  const { selectedProviders } = useStrategies(LOCATION.STRATEGY_CENTER);
  const hasSelectedProviders =
    selectedProviders && selectedProviders.length > 0;
  const [isSearchExpanded, setIsSearchExpanded] = useState(false);
  const searchRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);

  const handleClickOutside = useCallback(
    (event: MouseEvent) => {
      if (
        searchRef.current &&
        !searchRef.current.contains(event.target as Node) &&
        !search
      ) {
        setIsSearchExpanded(false);
      }
    },
    [search]
  );

  useEffect(() => {
    if (isSearchExpanded) {
      document.addEventListener('mousedown', handleClickOutside);
      searchInputRef.current?.focus();
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isSearchExpanded, handleClickOutside]);

  const toggleableColumns = useMemo(() => {
    const fromStatic = strategyListTableColumns
      .filter(Boolean)
      .filter((col) => STRATEGY_TOGGLEABLE_COLUMNS.includes(col.field));

    // Strategy column is added dynamically in StrategyList, not in strategyListTableColumns
    const strategyCol = {
      field: 'chain_ids',
      headerName: 'Strategy',
    };

    return [strategyCol, ...fromStatic];
  }, []);

  return (
    <div
      data-testid="strategy-center-header"
      className="w-full h-auto bg-white rounded-lg flex flex-col"
    >
      {/* First Row */}
      <div className="@container/header w-full px-4 py-3 mx-auto flex flex-row justify-between items-center gap-1 border-b border-neutral-200">
        <div
          data-testid="dropdowns-container"
          className="flex items-center gap-2"
        >
          <StrategyDropdown />
          <StrategyTerritoryDropdown />

          {/* View Toggle */}
          <div
            data-testid="view-toggle"
            role="radiogroup"
            aria-label="View toggle"
            className="relative inline-flex rounded-full bg-neutral-100 p-1"
          >
            <div
              className={`absolute top-1 bottom-1 w-[calc(50%-4px)] rounded-full bg-white shadow-sm transition-all duration-300 ease-in-out ${
                view === 'list' ? 'left-1' : 'left-[calc(50%)]'
              }`}
            />
            {VIEW_OPTIONS.map((option) => {
              const isActive = view === option.key;
              return (
                <button
                  key={option.key}
                  type="button"
                  data-testid={`view-toggle-${option.key}`}
                  className={`relative z-10 w-1/2 px-3.5 py-1.5 rounded-full inline-flex items-center justify-center gap-1.5 text-sm font-semibold transition-colors duration-300 ${
                    isActive
                      ? 'text-primary'
                      : 'text-tertiary hover:text-primary'
                  }`}
                  onClick={() => setView(option.key)}
                  aria-pressed={isActive}
                  aria-label={`${option.label} view`}
                >
                  <Icon icon={option.icon} className="w-4 h-4" />
                  <span className="hidden @[1000px]/header:inline">{option.label}</span>
                </button>
              );
            })}
          </div>
        </div>
        <StrategyInfoButton />
      </div>

      {/* Second Row - Filters */}
      <div
        data-testid="strategy-list-filters"
        className="@container/filters w-full px-4 py-3 flex items-center gap-2 border-b border-neutral-200"
      >
        <div className="flex items-center gap-2 flex-1 min-w-0">
          {/* Opportunities Count - List view only */}
          {view === 'list' && (
            <div
              data-testid="strategy-list-count"
              className="flex items-center gap-1 flex-shrink-0"
            >
              <div className="w-7 h-7 flex items-center justify-center">
                <Icon icon={faUserDoctor} className="text-primary" />
              </div>
              <div className="text-primary text-lg font-bold font-['Inter'] leading-7 flex items-center gap-2">
                {listCount.isFetching ? (
                  <>
                    <div role="status" aria-label="Loading count" className="h-5 w-12 bg-gradient-to-r from-neutral-200 via-neutral-100 to-neutral-200 rounded overflow-hidden relative inline-block">
                      <div className="absolute inset-0 -translate-x-full animate-[shimmer_2s_infinite] bg-gradient-to-r from-transparent via-white/60 to-transparent" />
                    </div>
                    <span className="hidden @[1400px]/filters:inline">Opportunities</span>
                  </>
                ) : (
                  <>
                    {listCount.total}
                    <span className="hidden @[1400px]/filters:inline">Opportunities</span>
                  </>
                )}
              </div>
            </div>
          )}
          <div ref={searchRef} className="flex-shrink-0">
            {isSearchExpanded ? (
              <StrategyProviderSearch
                search={search}
                onSearch={onSearchChange}
                inputRef={searchInputRef}
              />
            ) : (
              <button
                data-testid="search-toggle"
                type="button"
                onClick={() => setIsSearchExpanded(true)}
                className="w-8 h-8 rounded-full border border-neutral-300 bg-neutral-100 inline-flex items-center justify-center hover:bg-neutral-200 transition-colors cursor-pointer"
                aria-label="Search providers"
              >
                <Icon icon={faSearch} className="text-tertiary text-xs" />
              </button>
            )}
          </div>
          <StrategyStatusDropdown />
          <StrategySpecialtyDropdown />
          <StrategySiteOfCareTypeDropdown />
          <StrategyCityDropdown />
          <StrategyStateDropdown />
          <StrategyProviderListDropdown />
        </div>
        {view === 'list' && (
          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              data-testid="strategy-list-add-to-list-button"
              className={`p-2 @[1300px]/filters:px-3.5 @[1300px]/filters:py-2 rounded-full border inline-flex items-center gap-1 ${
                hasSelectedProviders
                  ? 'bg-white border-neutral-200 hover:bg-neutral-100 cursor-pointer'
                  : 'bg-white border-neutral-200 opacity-50 cursor-not-allowed'
              }`}
              onClick={
                hasSelectedProviders
                  ? () => {
                      onAddToList();
                      log.event('Strategy list add to list clicked', {
                        source: 'StrategyCenterHeader',
                      });
                    }
                  : undefined
              }
              type="button"
              aria-label="Add to List"
              disabled={!hasSelectedProviders}
            >
              <div className="w-4 h-4 flex items-center justify-center flex-shrink-0">
                <Icon icon={faPlus} className="text-primary" size="sm" />
              </div>
              <span className="hidden @[1300px]/filters:inline text-primary text-sm font-semibold leading-tight whitespace-nowrap">
                Add to List
              </span>
            </button>
            <TableColumnMenu
              table="strategy-list"
              columns={toggleableColumns}
              columnVisibilityModel={savedColumnVisibility}
              setColumnVisibilityModel={onColumnVisibilityChange}
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default StrategyCenterHeader;
